import { makeAutoObservable, runInAction } from 'mobx';
import type { RootStore } from './root-store';
// eslint-disable-next-line import/no-cycle
import { PersonalAssetStore } from './personal-asset-store';
import { CreatePersonalAssetDto, UpdatePersonalAssetDto } from '../../defs/api';
import { PersonalAssetWithValueMobxDto } from '../mobx/dtos/personal-assets/personal-asset-with-value-mobx-dto';

export class PersonalAssetDialogStore {
    rootStore: RootStore;

    personalAssetStore: PersonalAssetStore;

    createDialogOpened = false;

    updateDialogOpened = false;

    updatingInitialValues?: PersonalAssetWithValueMobxDto;

    loading = false;

    constructor(personalAssetStore: PersonalAssetStore) {
        this.rootStore = personalAssetStore.rootStore;
        this.personalAssetStore = personalAssetStore;

        makeAutoObservable(this, {
            rootStore: false,
            personalAssetStore: false,
        });
    }

    startLoading(): void {
        this.loading = true;
    }

    stopLoading(): void {
        this.loading = false;
    }

    async create(data: CreatePersonalAssetDto): Promise<void> {
        try {
            this.startLoading();
            await this.rootStore.apiClient.personalAssetsApi.personalAssetControllerCreate({
                createPersonalAssetDto: data,
            });
            await this.personalAssetStore.fetchPersonalAssetList(data.portfolioId);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }

        this.closeCreateDialog();
    }

    async update(id: string, data: UpdatePersonalAssetDto): Promise<void> {
        try {
            this.startLoading();
            await this.rootStore.apiClient.personalAssetsApi.personalAssetControllerUpdate({
                updatePersonalAssetDto: data,
                id,
            });
            await this.personalAssetStore.fetchPersonalAssetList(data.portfolioId);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            this.stopLoading();
        }

        this.closeCreateDialog();
    }

    openCreateDialog(): void {
        this.rootStore.alertStore.setModalPlacement();
        this.createDialogOpened = true;
    }

    closeCreateDialog(): void {
        this.createDialogOpened = false;
        this.rootStore.alertStore.setDefaultPlacement();
    }

    openUpdateDialog(updatingInitialValues: PersonalAssetWithValueMobxDto): void {
        this.updatingInitialValues = updatingInitialValues;
        this.rootStore.alertStore.setModalPlacement();
        this.updateDialogOpened = true;
    }

    closeUpdateDialog(): void {
        this.updateDialogOpened = false;
        this.updatingInitialValues = undefined;
        this.rootStore.alertStore.setDefaultPlacement();
    }

    async deletePersonalAsset(assetId: string): Promise<void> {
        this.startLoading();
        try {
            if (!this.updatingInitialValues) {
                return;
            }
            await this.rootStore.apiClient.personalAssetsApi.personalAssetControllerDelete({
                id: assetId,
            });
            await this.personalAssetStore.fetchPersonalAssetList(this.updatingInitialValues.portfolioId);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(e);
            this.rootStore.alertStore.setErrorMessageByStatus(e.message);
        } finally {
            runInAction(() => {
                this.updateDialogOpened = false;
            });
            this.stopLoading();
        }
    }
}
